import { useRedirectContext } from 'components/common/withRedirect';
import { Spinner } from 'components/core';
import { useCompanies } from 'store/company';
import { useContracts } from 'store/contract';
import {
  useInspection,
  useInspectionTemplateOnView,
  useInspectionFieldDisabled,
} from 'store/inspection';
import { useInspectionIssues, useInspectionRelatedIssuesCount } from 'store/issue';
import { useSites } from 'store/site';
import { filterFieldValuesByProcess } from 'presentation/fieldValue/filterFieldValues';
import React, {
  ReactElement,
  useCallback,
  useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys, FieldTranslationKeys, InspectionTranslationKeys } from 'intl/keys';
import { translationKeyToString } from 'intl/types/translationKey';

import { useProjectTimezone } from 'store/project';
import { HashMap, LabelledEntity } from 'shared/types/commonView';
import { DayMomentValidation } from 'shared/types/time';
import {
  contractsExtractor,
  controlledPartiesExtractor,
  toLabelledEntity,
} from './model';
import { InspectionFormPresentational } from './presentational';
import {
  InspectionFieldName,
  InspectionFieldProps,
  InspectionFormProps,
  InspectionFormSection,
} from './types';

import {
  useLevelsToSelect,
} from 'store/level';
import { keepUndeleted } from 'shared/domain/deletable/filters';
import {
  EditableFieldValuesEntityName,
  EditableStandardEntityName,
} from 'shared/domain/fieldValue/fields';
import { WorktypeOnView } from 'presentation/fieldValue/fieldValueOnView';
import { getLevelsForMultipleSites } from 'presentation/level/levelExtractor';
import { nowTimezonedDateTime } from 'shared/utils/date/dates';
import { useWorktypes } from 'store/fieldValue';

function InspectionForm({
  loading,
  onSubmit,
  onCancel,
  isSaving = false,
  atomicMode = false,
  editPermission,
  actionButtons,
  inspectionStatus,
  onStatusChange,
}: InspectionFormProps): ReactElement {
  const intl = useIntl();
  const { items: workTypes } = useWorktypes();
  const timezone = useProjectTimezone();
  const { setCanRedirect } = useRedirectContext();
  const { template } = useInspectionTemplateOnView();
  const processWorkTypes = useMemo(() => {
    return filterFieldValuesByProcess(
      workTypes,
      template?.process
    );
  }, [workTypes, template?.process]);
  const { inspection, inspectionModel } = useInspection();
  const isArchived = Boolean(inspection?.deleted);
  const relatedIssuesCount = useInspectionRelatedIssuesCount(inspectionModel);
  const { issues: inspectionIssues } = useInspectionIssues();

  const { items: companies, loading: loadingCompanies } = useCompanies();
  const { items: contracts, loading: loadingContracts } = useContracts();

  const {
    items: sites,
    loading: loadingSites,
  } = useSites();
  const { levelsPerSite } = useLevelsToSelect();

  const companiesPerSite = useMemo(() => {
    if (!sites || loadingSites || loadingCompanies || !companies)
      return {};
    const companiesOnSite = sites.reduce<HashMap<LabelledEntity[]>>(
      (map, site) => {
        map[site._id] = [];
        return map;
      },
      {}
    );
    companies.forEach((company) => {
      company.sites.forEach((site) => {
        if (Array.isArray(companiesOnSite[site])) {
          companiesOnSite[site].push({
            _id: company._id,
            label: company.shortLabel,
          });
        }
      });
    }, {});
    return companiesOnSite;
  }, [companies, sites, loadingSites, loadingCompanies]);

  const contractsPerCompany = useMemo(() => {
    if (loadingContracts || !contracts) return {};
    return contracts.reduce<HashMap<LabelledEntity[]>>(
      (result, contract): HashMap<LabelledEntity[]> => {
        contract.parties.forEach((party) => {
          const contractLabelledEntity = {
            _id: contract._id,
            label: contract.label,
          };

          if (Array.isArray(result[party.companyId])) {
            result[party.companyId].push(contractLabelledEntity);
          } else {
            result[party.companyId] = [contractLabelledEntity];
          }
        });

        return result;
      },
      {}
    );
  }, [contracts, loadingContracts]);

  const isFieldDisabled = useInspectionFieldDisabled();

  const executionDateFieldProps: InspectionFieldProps = useMemo(() => {
    return {
      formKey: 'inspectionDate',
      required: false,
      labelId: InspectionTranslationKeys.executionDate,
      fieldName: InspectionFieldName.inspectionDate,
      disabled: isFieldDisabled(false),
      timezone: timezone,
      daymoment: DayMomentValidation.start,
      defaultValue: nowTimezonedDateTime(
        timezone,
        DayMomentValidation.start
      ),
      reserveSpaceForHelperText: true,
      dense: true,
      'data-qa': 'inspection_execution_date_field',
      issueParams: null,
    };
  }, [timezone, isFieldDisabled]);

  const workTypesFieldProps: InspectionFieldProps = useMemo(() => {
    return {
      formKey: 'workTypes',
      required: false,
      entityName: EditableFieldValuesEntityName.workTypes,
      labelId: InspectionTranslationKeys.tableWorkType,
      fieldName: InspectionFieldName.multiselectWorkTypes,
      available: processWorkTypes,
      disabled: isFieldDisabled(false),
      reserveSpaceForHelperText: true,
      dense: true,
      'data-qa': 'inspection_worktypes_field',
      issueParams: null,
    };
  }, [processWorkTypes, isFieldDisabled]);

  const availableSites = useMemo(
    () => sites.filter(keepUndeleted).map(toLabelledEntity),
    [sites]
  );

  const sitesFieldProps: InspectionFieldProps = useMemo(() => {
    const anyIssuesInProtocol = inspectionIssues.length > 0;

    return {
      formKey: 'sites',
      required: true,
      labelId: FieldTranslationKeys.site,
      entityName: EditableStandardEntityName.location,
      fieldName: InspectionFieldName.multiselectSites,
      available: availableSites,
      disabled: isFieldDisabled(
        anyIssuesInProtocol
          ? {
              reason: translationKeyToString(
                InspectionTranslationKeys.formIssueSiteEditWarning
              ),
            }
          : false
      ),
      autoSelect: 'asMultiSelect',
      maxVisibleTags: 2,
      reserveSpaceForHelperText: true,
      dense: true,
      'data-qa': 'inspection_sites_field',
      issueParams: null,
    };
  }, [availableSites, inspectionIssues.length, isFieldDisabled]);

  const levelsFieldProps: InspectionFieldProps = useMemo(() => {
    return {
      formKey: 'levels',
      required: true,
      labelId: FieldTranslationKeys.level,
      entityName: EditableStandardEntityName.level,
      fieldName: InspectionFieldName.multiselectLevels,
      available: levelsPerSite,
      dependsOn: 'sites',
      extractor: getLevelsForMultipleSites,
      groupBy: (option: { group?: string }): string => option.group ?? '',
      disabled: isFieldDisabled(false),
      autoSelect: 'asMultiSelect',
      reserveSpaceForHelperText: true,
      dense: true,
      'data-qa': 'inspection_level_field',
      issueParams: null,
    };
  }, [levelsPerSite, isFieldDisabled]);

  const controlledPartiesFieldProps: InspectionFieldProps = useMemo(() => {
    return {
      formKey: 'controlledParty',
      required: false,
      labelId: InspectionTranslationKeys.controlledParty,
      field: {
        name: InspectionFieldName.multiselectControlledParties,
      },
      fieldName: InspectionFieldName.multiselectControlledParties,
      entityName: EditableStandardEntityName.company,
      available: companiesPerSite,
      dependsOn: 'sites',
      extractor: controlledPartiesExtractor,
      disabled: isFieldDisabled(false),
      reserveSpaceForHelperText: true,
      dense: true,
      'data-qa': 'inspection_controlled_party_field',
      issueParams: null,
    };
  }, [companiesPerSite, isFieldDisabled]);

  const contractsFieldProps: InspectionFieldProps = useMemo(() => {
    return {
      formKey: 'contracts',
      required: false,
      labelId: InspectionTranslationKeys.contractNumber,
      fieldName: InspectionFieldName.multiselectContracts,
      entityName: EditableStandardEntityName.contract,
      available: contractsPerCompany,
      dependsOn: 'controlledParty',
      extractor: contractsExtractor,
      disabled: isFieldDisabled(false),
      reserveSpaceForHelperText: true,
      dense: true,
      'data-qa': 'inspection_contract_number_field',
      issueParams: null,
    };
  }, [contractsPerCompany, isFieldDisabled]);

  const commentFieldProps: InspectionFieldProps = useMemo(() => {
    return {
      formKey: 'comment',
      required: false,
      labelId: InspectionTranslationKeys.comment,
      fieldName: InspectionFieldName.textFieldComment,
      disabled: isFieldDisabled(false),
      localStorageKey: 'inspection-comment-html',
      reserveSpaceForHelperText: true,
      dense: true,
      'data-qa': 'inspection_comment_field',
      multiline: true,
      minRows: 3,
      issueParams: null,
      placeholder: formatTranslation(
        intl,
        InspectionTranslationKeys.commentAddPlaceholder
      ),
    };
  }, [intl, isFieldDisabled]);

  const submitWithRedirect = useCallback(() => {
    setCanRedirect(true);
    onSubmit?.();
  }, [setCanRedirect, onSubmit]);


  const checklist = useMemo(
    () =>
      template
        ? template.checklist.filter((checklistItem) => {
            return !checklistItem.deleted;
          })
        : [],
    [template]
  );

  const sections = useMemo(
    (): InspectionFormSection[] => {
      const templateSections = template
        ? template.sections.filter((section) => {
            return !section.deleted;
          })
        : [];

      if (!inspection?.sections?.length) {
        return templateSections.map((section) => ({
          ...section,
          inspectionDescription: '',
        }));
      }

      const inspectionSectionById = new Map(
        inspection.sections.map((section) => [section._id, section])
      );
      const inspectionSectionByChecklistSectionId = new Map(
        inspection.sections
          .filter((section) => Boolean(section.checklistSectionId))
          .map((section) => [section.checklistSectionId as string, section])
      );
      const inspectionSectionByOrder = new Map(
        inspection.sections.map((section) => [section.order, section])
      );

      return templateSections.map((section) => ({
        ...section,
        inspectionDescription:
          inspectionSectionByChecklistSectionId.get(section._id)?.comment ||
          inspectionSectionById.get(section._id)?.comment ||
          inspectionSectionByOrder.get(section.order)?.comment ||
          '',
      }));
    },
    [inspection?.sections, template]
  );

  const isLoadingDependencies =
    loadingSites || loadingCompanies || loadingContracts;

  if (loading || isLoadingDependencies || !template) {
    return (
      <Spinner
        reason={`InspectionForm loading: ${loading} || isLoadingDependencies: ${isLoadingDependencies} || !template: ${!template}`}
      />
    );
  }

  return (
    <InspectionFormPresentational
      template={template}
      onSubmit={submitWithRedirect}
      onCancel={onCancel}
      isSaving={isSaving}
      isNewInspection={!inspection}
      inspectionCode={inspection?.code}
      atomicMode={atomicMode}
      editPermission={editPermission}
      actionButtons={actionButtons}
      inspectionStatus={inspectionStatus}
      onStatusChange={onStatusChange}
      isArchived={isArchived}
      executionFieldProps={executionDateFieldProps}
      workTypesFieldProps={workTypesFieldProps}
      sitesFieldProps={sitesFieldProps}
      levelsFieldProps={levelsFieldProps}
      controlledPartiesFieldProps={controlledPartiesFieldProps}
      contractsFieldProps={contractsFieldProps}
      commentFieldProps={commentFieldProps}
      checklist={checklist}
      sections={sections}
      protocolIssues={relatedIssuesCount}
    />
  );
}

export const MemoInspectionForm = React.memo(InspectionForm);
